import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  RefreshCcw,
  LayoutGrid,
  Table2,
  Pencil,
  Trash2,
  Search,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";
import "./RemindersDashboard.css";
import api from "../utils/api";
import { io } from "socket.io-client";
import KuddleSelect from "../components/KuddleSelect";

async function apiUpdateReminder(id, payload) {
  const res = await api.put(`/api/reminders/${id}`, payload);
  return res.data;
}

async function apiDeleteReminder(id) {
  const res = await api.delete(`/api/reminders/${id}`);
  return res.data;
}

async function apiRemindAgain(id) {
  const res = await api.post(`/api/reminders/remind-again/${id}`);
  return res.data;
}

async function apiCompleteReminder(id) {
  const res = await api.patch(`/api/reminders/${id}/complete`);
  return res.data;
}

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtTarget(value) {
  if (!value) return "—";
  const text = String(value);
  if (/^[a-f\d]{24}$/i.test(text)) return "Assigned group";
  return text;
}

function getReminderPersonName(r) {
  if (!r) return "—";

  const completedUser =
    r.completedBy?.name ||
    r.completedBy?.fullName ||
    r.completedBy?.email;

  if (
    r.targetName === "Me" ||
    r.targetName === "Myself" ||
    r.audience === "individual"
  ) {
    return completedUser || "Lynn";
  }

  return r.targetName || fmtTarget(r.target);
}

function toDateKey(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getBucket(remindAt) {
  if (!remindAt) return "upcoming";

  const now = new Date();
  const r = new Date(remindAt);
  if (Number.isNaN(r.getTime())) return "upcoming";

  const todayKey = toDateKey(now);
  const rKey = toDateKey(r);

  if (r < now && rKey !== todayKey) return "overdue";
  if (rKey === todayKey) return "today";
  if (r > now) return "upcoming";

  return "upcoming";
}

function scrollToSection(key) {
  const el = document.getElementById(`section-${key}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function emailStatusLabel(status) {
  if (status === "sent") return "Email: Sent";
  if (status === "failed") return "Email: Failed";
  if (status === "skipped") return "Email: Skipped";
  return "Email: —";
}

const RemindersDashboard = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const [search, setSearch] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [viewMode, setViewMode] = useState("board");

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [historyReminder, setHistoryReminder] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editMessage, setEditMessage] = useState("");
  const [editAudience, setEditAudience] = useState("role");
  const [editTarget, setEditTarget] = useState("staff");
  const [editRemindAt, setEditRemindAt] = useState("");
  const [editPriority, setEditPriority] = useState("medium");

  const loadReminders = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get("/api/reminders");
      setReminders(Array.isArray(res.data) ? res.data : []);
      setLastRefreshedAt(new Date());
    } catch (err) {
      console.error("Load reminders failed:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getStatus = (date) => {
    const now = new Date();
    const d = new Date(date);

    if (d < now) return "overdue";

    const today = new Date();
    if (d.toDateString() === today.toDateString()) return "today";

    return "upcoming";
  };

  useEffect(() => {
    loadReminders();
  }, []);

  useEffect(() => {
    const refreshReminders = () => {
      loadReminders(true);
    };

    window.addEventListener("kuddle:reminders-updated", refreshReminders);

    return () => {
      window.removeEventListener("kuddle:reminders-updated", refreshReminders);
    };
  }, []);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL || "");

    socket.on("newReminder", (reminder) => {
      setReminders((prev) => [reminder, ...prev]);
    });

    socket.on("updatedReminder", (updated) => {
      setReminders((prev) =>
        prev.map((r) => (r._id === updated._id ? updated : r))
      );
    });

    socket.on("deletedReminder", ({ _id }) => {
      setReminders((prev) => prev.filter((r) => r._id !== _id));
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      loadReminders(true);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const priorityRank = { high: 3, medium: 2, low: 1 };

    return reminders
      .filter((r) => {
        if (audienceFilter !== "all" && r.audience !== audienceFilter) {
          return false;
        }

        if (priorityFilter !== "all" && r.priority !== priorityFilter) {
          return false;
        }

        if (!q) return true;

        const hay = [
          r.message,
          r.audience,
          r.target,
          r.fromNoteId,
          r.emailStatus,
          Array.isArray(r.deliveredTo) ? r.deliveredTo.join(" ") : "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return hay.includes(q);
      })
      .sort((a, b) => {
        const p =
          (priorityRank[b.priority] || 2) -
          (priorityRank[a.priority] || 2);

        if (p !== 0) return p;

        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });
  }, [reminders, search, audienceFilter, priorityFilter]);

  const buckets = useMemo(() => {
    const out = {
      overdue: [],
      today: [],
      upcoming: [],
      completed: [],
    };

    for (const r of filtered) {
      if (r.status === "completed") {
        out.completed.push(r);
      } else {
        out[getBucket(r.remindAt)].push(r);
      }
    }

    out.overdue.sort(
      (a, b) =>
        new Date(a.remindAt || a.createdAt || 0) -
        new Date(b.remindAt || b.createdAt || 0)
    );

    out.today.sort(
      (a, b) => new Date(a.remindAt || 0) - new Date(b.remindAt || 0)
    );

    out.upcoming.sort(
      (a, b) => new Date(a.remindAt || 0) - new Date(b.remindAt || 0)
    );

    out.completed.sort(
      (a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0)
    );

    return out;
  }, [filtered]);

  const openHistory = async (r) => {
    if (!r?._id) return;

    try {
      const res = await api.get(`/api/reminders/${r._id}/history`);
      setHistoryReminder(r);
      setHistoryLogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load reminder history:", err);
      toast.error("Failed to load reminder history");
    }
  };

  const openEdit = (r) => {
    setEditId(r._id);
    setEditMessage(r.message || "");
    setEditAudience(r.audience || "role");
    setEditTarget(r.target || "");
    setEditPriority(r.priority || "medium");

    const dt = r.remindAt ? new Date(r.remindAt) : null;

    setEditRemindAt(
      dt && !Number.isNaN(dt.getTime()) ? dt.toISOString().slice(0, 16) : ""
    );

    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditId(null);
    setEditMessage("");
    setEditAudience("role");
    setEditTarget("staff");
    setEditRemindAt("");
  };

  const saveEdit = async () => {
    if (!editId) return;

    const msg = editMessage.trim();

    if (!msg) {
      toast.error("Message is required.");
      return;
    }

    try {
      const payload = {
        message: msg,
        audience: editAudience,
        target: editTarget,
        remindAt: editRemindAt ? new Date(editRemindAt).toISOString() : null,
        priority: editPriority,
      };

      await apiUpdateReminder(editId, payload);
      await loadReminders();
      closeEdit();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Update failed");
    }
  };

  const markDone = (r) => {
    if (!r?._id) return;

    setConfirmAction({
      type: "done",
      title: "Mark reminder as completed?",
      message: "This will mark the reminder completed and keep it for history.",
      reminder: r,
    });
  };

  const handleRefresh = () => {
    if (!loading) loadReminders();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const selectVisible = () => {
    setSelectedIds(filtered.map((r) => r._id));
  };

  const bulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.info("Select reminders first.");
      return;
    }

    setConfirmAction({
      type: "bulk-delete",
      title: "Delete selected reminders?",
      message: `This will delete ${selectedIds.length} selected reminder(s).`,
      reminder: null,
    });
  };

  const markAllOverdueDone = () => {
    const overdueIds = filtered
      .filter((r) => r.status !== "completed")
      .filter((r) => getStatus(r.remindAt) === "overdue")
      .map((r) => r._id);

    if (overdueIds.length === 0) {
      toast.info("No overdue reminders.");
      return;
    }

    setConfirmAction({
      type: "bulk-overdue-delete",
      title: "Delete all overdue reminders?",
      message: `This will delete ${overdueIds.length} overdue reminder(s).`,
      ids: overdueIds,
    });
  };

  const deleteOne = (r) => {
    if (!r?._id) return;

    setConfirmAction({
      type: "delete",
      title: "Delete reminder?",
      message: "This reminder will be removed from the list.",
      reminder: r,
    });
  };

  const handleConfirmAction = async () => {
    try {
      if (
        confirmAction?.type === "bulk-delete" ||
        confirmAction?.type === "bulk-overdue-delete"
      ) {
        const idsToDelete =
          confirmAction.type === "bulk-overdue-delete"
            ? confirmAction.ids
            : selectedIds;

        await Promise.all(idsToDelete.map((id) => apiDeleteReminder(id)));
        await loadReminders();
        setSelectedIds([]);
        setConfirmAction(null);
        return;
      }

      if (!confirmAction?.reminder?._id) return;

      if (confirmAction?.type === "done") {
        await apiCompleteReminder(confirmAction.reminder._id);
        await loadReminders();
        setConfirmAction(null);
        return;
      }

      await apiDeleteReminder(confirmAction.reminder._id);
      await loadReminders();
      setConfirmAction(null);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Action failed");
    }
  };

  const remindAgain = async (r) => {
    if (!r?._id) return;

    try {
      const resp = await apiRemindAgain(r._id);
      const updated = resp?.reminder;

      if (updated?._id) {
        await loadReminders();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Remind again failed");
    }
  };

  const BoardSection = ({ idKey, title, tone, items }) => (
    <div id={`section-${idKey}`} className={`rsection rsection-${tone}`}>
      <div className="rsection-head">
        <h2 className="rsection-title">{title}</h2>
        <span className="meta-pill">{items.length}</span>
      </div>

      {items.length === 0 ? (
        <div className="empty-state small">Nothing here.</div>
      ) : (
        <div className="rcards">
          {items.map((r) => (
            <div
              key={r._id}
              className={`rcard rcard-${tone} ${selectedIds.includes(r._id) ? "selected" : ""
                }`}
            >
              {r.status !== "completed" && (
                <label className="reminder-select">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r._id)}
                    onChange={() => toggleSelect(r._id)}
                  />
                  Select
                </label>
              )}

              <div className="rcard-top">
                {r.status === "completed" ? (
                  <span className="completed-chip">✓ Completed</span>
                ) : (
                  <span className={`priority-pill priority-${r.priority || "medium"}`}>
                    {(r.priority || "medium").charAt(0).toUpperCase() +
                      (r.priority || "medium").slice(1)}
                  </span>
                )}

                {tone === "danger" && (
                  <span className={`status-badge status-${getStatus(r.remindAt)}`}>
                    {getStatus(r.remindAt).charAt(0).toUpperCase() +
                      getStatus(r.remindAt).slice(1)}
                  </span>
                )}

                {r.resendCount > 0 && (
                  <span className="resent-badge">Resent {r.resendCount}</span>
                )}
              </div>

              <div className="rcard-msg">{r.message}</div>

              <div className="rcard-meta">
                <div>
                  <span className="meta-k">Assigned To:</span>{" "}
                  <span className="meta-v">{getReminderPersonName(r)}</span>
                </div>

                <div>
                  <span className="meta-k">Remind:</span>{" "}
                  <span className="meta-v">{fmtDate(r.remindAt)}</span>
                </div>

                <div>
                  <span className="meta-k">Created:</span>{" "}
                  <span className="meta-v">{fmtDate(r.createdAt)}</span>
                </div>

                {r.status === "completed" && (
                  <>
                    <div>
                      <span className="meta-k">Completed By:</span>{" "}
                      <span className="meta-v">
                        {r.completedBy?.name ||
                          r.completedBy?.fullName ||
                          r.completedBy?.email ||
                          "Unknown"}
                      </span>
                    </div>

                    <div>
                      <span className="meta-k">Completed On:</span>{" "}
                      <span className="meta-v">{fmtDate(r.completedAt)}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="rcard-actions">
                {r.status !== "completed" ? (
                  <>
                    <button
                      className="rbtn rbtn-ghost"
                      onClick={() => openEdit(r)}
                      type="button"
                    >
                      <Pencil size={16} /> Edit
                    </button>

                    <button
                      className="rbtn rbtn-ghost"
                      onClick={() => remindAgain(r)}
                      type="button"
                    >
                      <RotateCcw size={16} /> Resend
                    </button>

                    <button
                      className="rbtn rbtn-done"
                      onClick={() => markDone(r)}
                      type="button"
                    >
                      <CheckCircle2 size={16} /> Done
                    </button>

                    <button
                      className="rbtn rbtn-danger"
                      onClick={() => deleteOne(r)}
                      type="button"
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="rbtn rbtn-ghost"
                      onClick={() => openHistory(r)}
                      type="button"
                    >
                      View History
                    </button>

                    <button
                      className="rbtn rbtn-danger"
                      onClick={() => deleteOne(r)}
                      type="button"
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="reminders-page">
      <div className="reminders-header">
        <div>
          <h1 className="reminders-title">Reminders</h1>

          <p className="reminders-subtitle">
            View, edit, resend, and mark reminders as done.
          </p>
        </div>

        <div className="reminders-header-actions">
          <button
            type="button"
            className="rbtn-icon"
            onClick={handleRefresh}
            disabled={loading}
            aria-disabled={loading}
            title="Refresh reminders"
            aria-label="Refresh reminders"
          >
            <RefreshCcw
              size={24}
              className={loading ? "spin-icon" : ""}
            />

            <span className="home-tile-label">
              Refresh
            </span>
          </button>

          <div className="header-nav-group" role="group" aria-label="View mode">
            <button
              type="button"
              className={`vt-btn ${viewMode === "board" ? "active" : ""}`}
              onClick={() => setViewMode("board")}
            >
              <LayoutGrid size={16} /> Board
            </button>

            <button
              type="button"
              className={`vt-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
            >
              <Table2 size={16} /> Table
            </button>
          </div>
        </div>
      </div>

      <div className="reminders-filters">
        <div className="filter-group search-filter-group">
          <label className="filter-label">Search</label>

          <div className="filter-search-shell">
            <Search size={20} className="filter-search-icon" />

            <input
              className="filter-input filter-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search message, audience, target..."
            />
          </div>
        </div>

        <div className="filter-group audience-filter-group">
          <KuddleSelect
            label="Audience"
            value={audienceFilter}
            onChange={setAudienceFilter}
            options={[
              { value: "all", label: "All" },
              { value: "role", label: "Role" },
              { value: "classroom", label: "Classroom" },
              { value: "individual", label: "Individual" },
              { value: "parent", label: "Parent" },
            ]}
          />
        </div>

        <div className="filter-group priority-filter-group">
          <KuddleSelect
            label="Priority"
            value={priorityFilter}
            onChange={setPriorityFilter}
            options={[
              { value: "all", label: "All" },
              { value: "high", label: "High" },
              { value: "medium", label: "Medium" },
              { value: "low", label: "Low" },
            ]}
          />
        </div>

      </div>

      <div className="bulk-actions-row">
        <span className="bulk-count">Selected: {selectedIds.length}</span>

        <button className="bulk-action-btn" type="button" onClick={selectVisible}>
          Select All Shown
        </button>

        <button className="bulk-action-btn" type="button" onClick={clearSelection}>
          Clear
        </button>

        <button
          className="bulk-action-btn bulk-action-danger"
          type="button"
          onClick={bulkDelete}
        >
          Delete Selected
        </button>
      </div>

      {loading ? (
        <div className="reminders-card">
          <div className="empty-state">Loading reminders…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="reminders-card">
          <div className="empty-state">
            No reminders yet. Promote a note from Notepad to get started.
          </div>
        </div>
      ) : viewMode === "board" ? (
        <>
          <div className="director-summary">
            <button
              type="button"
              className="summary-pill summary-overdue"
              onClick={() => scrollToSection("overdue")}
            >
              {buckets.overdue.length} Overdue
            </button>

            <button
              type="button"
              className="summary-pill summary-today"
              onClick={() => scrollToSection("today")}
            >
              {buckets.today.length} Today
            </button>

            <button
              type="button"
              className="summary-pill summary-upcoming"
              onClick={() => scrollToSection("upcoming")}
            >
              {buckets.upcoming.length} Upcoming
            </button>

            <button
              type="button"
              className="summary-pill summary-completed"
              onClick={() => scrollToSection("completed")}
            >
              {buckets.completed.length} Completed
            </button>
          </div>

          <div className="rboard">
            <BoardSection
              idKey="overdue"
              title="Overdue"
              tone="danger"
              items={buckets.overdue}
            />

            <BoardSection
              idKey="today"
              title="Today"
              tone="warning"
              items={buckets.today}
            />

            <BoardSection
              idKey="upcoming"
              title="Upcoming"
              tone="info"
              items={buckets.upcoming}
            />

            <BoardSection
              idKey="completed"
              title="Completed"
              tone="success"
              items={buckets.completed}
            />
          </div>
        </>
      ) : (
        <div className="reminders-card">
          <table className="reminders-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Message</th>
                <th>Audience</th>
                <th>Target</th>
                <th>Remind At</th>
                <th>Created</th>
                <th>Email</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => {
                const delivered = Array.isArray(r.deliveredTo)
                  ? r.deliveredTo
                  : [];
                const deliveredText = delivered.length
                  ? delivered.join(", ")
                  : "—";
                const statusText = emailStatusLabel(r.emailStatus);

                return (
                  <tr
                    key={r._id}
                    className={selectedIds.includes(r._id) ? "selected-row" : ""}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(r._id)}
                        onChange={() => toggleSelect(r._id)}
                      />
                    </td>

                    <td>
                      <span className={`status-pill status-${getBucket(r.remindAt)}`}>
                        {getBucket(r.remindAt)}
                      </span>
                    </td>

                    <td>
                      <span className={`priority-pill priority-${r.priority || "medium"}`}>
                        {(r.priority || "medium").charAt(0).toUpperCase() +
                          (r.priority || "medium").slice(1)}
                      </span>
                    </td>

                    <td className="msg-cell">{r.message}</td>

                    <td>
                      <span className={`badge badge-${r.audience || "default"}`}>
                        {r.audience || "—"}
                      </span>

                      {r.resendCount > 0 && (
                        <span className="resent-badge">
                          🔁 Resent {r.resendCount}
                        </span>
                      )}
                    </td>

                    <td className="target-cell">{fmtTarget(r.target)}</td>

                    <td>{fmtDate(r.remindAt)}</td>

                    <td>{fmtDate(r.createdAt)}</td>

                    <td
                      title={`Delivered to: ${deliveredText}${r.emailError ? ` | Error: ${r.emailError}` : ""
                        }`}
                    >
                      {statusText}
                    </td>

                    <td className="actions-col">
                      <button
                        className="rbtn rbtn-ghost"
                        onClick={() => openEdit(r)}
                        type="button"
                      >
                        Edit
                      </button>

                      <button
                        className="rbtn rbtn-ghost"
                        onClick={() => remindAgain(r)}
                        type="button"
                      >
                        Remind Again
                      </button>

                      <button
                        className="rbtn rbtn-danger"
                        onClick={() => deleteOne(r)}
                        type="button"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isEditOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Reminder</h2>

              <button
                className="rbtn rbtn-ghost"
                onClick={closeEdit}
                type="button"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <label className="modal-label">Message</label>

              <textarea
                className="modal-textarea"
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                rows={4}
              />

              <div className="modal-grid">
                <div>
                  <label className="modal-label">Audience</label>

                  <select
                    className="modal-input"
                    value={editAudience}
                    onChange={(e) => setEditAudience(e.target.value)}
                  >
                    <option value="role">Role</option>
                    <option value="classroom">Classroom</option>
                    <option value="individual">Individual</option>
                    <option value="parent">Parent</option>
                    <option value="all">All</option>
                  </select>
                </div>

                <div>
                  <label className="modal-label">Target</label>

                  <input
                    className="modal-input"
                    value={editTarget}
                    onChange={(e) => setEditTarget(e.target.value)}
                    placeholder='e.g. "staff" or "Room A" or "email:someone@x.com"'
                  />
                </div>

                <div>
                  <label className="modal-label">Remind At</label>

                  <input
                    className="modal-input"
                    type="datetime-local"
                    value={editRemindAt}
                    onChange={(e) => setEditRemindAt(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="rbtn rbtn-secondary"
                onClick={closeEdit}
                type="button"
              >
                Cancel
              </button>

              <button
                className="rbtn rbtn-primary"
                onClick={saveEdit}
                type="button"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {historyReminder && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h2>Reminder History</h2>

              <button
                className="rbtn rbtn-ghost"
                onClick={() => setHistoryReminder(null)}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="modal-body reminder-history-summary">
              <div className="history-row">
                <span className="history-label">Message:</span>
                <span className="history-value">{historyReminder.message}</span>
              </div>

              <div className="history-row">
                <span className="history-label">Assigned To:</span>
                <span className="history-value">{getReminderPersonName(historyReminder)}</span>
              </div>

              <div className="history-row">
                <span className="history-label">Created:</span>
                <span className="history-value">{fmtDate(historyReminder.createdAt)}</span>
              </div>

              <div className="history-row">
                <span className="history-label">Completed By:</span>
                <span className="history-value">
                  {historyReminder.completedBy?.name ||
                    historyReminder.completedBy?.fullName ||
                    "Unknown"}
                </span>
              </div>

              <div className="history-row">
                <span className="history-label">Completed On:</span>
                <span className="history-value">{fmtDate(historyReminder.completedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal confirm-modal">
            <div className="modal-header">
              <h2>{confirmAction.title}</h2>

              <button
                className="rbtn rbtn-ghost"
                type="button"
                onClick={() => setConfirmAction(null)}
              >
                ✕
              </button>
            </div>

            <p className="confirm-message">{confirmAction.message}</p>

            <div className="modal-footer">
              <button
                className="rbtn rbtn-ghost"
                type="button"
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>

              <button
                className="rbtn rbtn-danger"
                type="button"
                onClick={handleConfirmAction}
              >
                {confirmAction?.type === "done"
                  ? "Mark Completed"
                  : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemindersDashboard;